import { ReactElement, useMemo } from 'react'
import { CellContext } from '@tanstack/react-table'
import { useLocation } from 'react-router-dom'

import { Button } from '@/ui/Button'
import { Popup } from '@/ui/Popup'
import { Disruption } from '@/types/disruptions'
import { useNotification } from '@/ui/Notification'
import { useCheckPermissions } from '@/permissions/useCheckPermissions'
import { useDeleteDisruption } from '../../api/useDeleteDisruption'
import { ROUTES } from '@/router'

import styles from './Actions.module.scss'

import ShareIcon from '../../assets/share.svg'
import ExpandIcon from '../../assets/expand.svg'
import DotsIcon from '../../assets/dots.svg'
import DeleteIcon from '../../assets/delete.svg'
import { useShareDisruption } from '@/pages/SharedDisruptions'

interface Action {
    label: string
    icon: ReactElement
    disabled?: boolean
    onClick: () => void
}

export const Actions = ({ row }: CellContext<Disruption, unknown>) => {
    const { showNotification } = useNotification()
    const hasPermission = useCheckPermissions()
    const { handleDeleteDisruption } = useDeleteDisruption(row.original)
    const { handleShareDisruption } = useShareDisruption(row.original)
    const { pathname } = useLocation()

    const actions = useMemo(() => {
        const results: Action[] = []

        if (hasPermission('disruption', 'viewPDF') && row.original.sourceLink) {
            results.push({
                label: 'View PDF',
                icon: <ExpandIcon />,
                onClick: () => {
                    window.open(row.original.sourceLink, '__blank')
                },
            })
        }

        if (hasPermission('disruption', 'share')) {
            results.push({
                label: 'Share',
                icon: <ShareIcon />,
                onClick: handleShareDisruption,
            })
        }

        if (
            hasPermission('disruption', 'delete') &&
            row.original.isUserCreated &&
            pathname.includes(ROUTES.ALL_DISRUPTIONS)
        ) {
            results.push({
                label: 'Delete',
                icon: <DeleteIcon />,
                onClick: handleDeleteDisruption,
            })
        }

        return results
    }, [hasPermission, handleDeleteDisruption, row.original, showNotification])

    if (!actions.length) {
        return <></>
    }

    return (
        <Popup
            className={styles.popup}
            triggerComponent={
                <Button className={styles.trigger} size="small" variant="transparent">
                    <DotsIcon />
                </Button>
            }
            offset={0}
            placement="bottom-end"
            closeOnClick
        >
            <div className={styles.actions}>
                {actions.map(({ label, icon, onClick }, index) => (
                    <Button key={index} className={styles.action} variant="transparent" size="small" onClick={onClick}>
                        <span className={styles.actionIcon}>{icon}</span>
                        <span>{label}</span>
                    </Button>
                ))}
            </div>
        </Popup>
    )
}
